import { useTranslations } from '@/i18n/use-translations';
import {
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Row } from './row';
import SubscriptionInvoiceRow from './subscription-invoice-row';
import { useEventsQuery } from '@/state/queries/events/use-events';
import { useOrganizationSubscriptionInvoicesQuery } from '@/state/queries/transactions/use-organization-subscription-invoices-query';
import { useMemo } from 'react';
import type { OrganizationSubscriptionInvoiceItemDto } from '@/types/dtos/transactions/subscription-invoice.dto';
import type { OrderDto } from '@/types/dtos/orders/response/order.dto';

type MergedRow =
  | { kind: 'order'; order: OrderDto; sortAt: number }
  | { kind: 'subscription'; item: OrganizationSubscriptionInvoiceItemDto; sortAt: number };

export default function InvoiceTable({ organizationId }: { organizationId: string }) {
  const t = useTranslations();
  const { data, isLoading: ordersLoading } = useEventsQuery(organizationId);
  const { data: subscriptionInvoices, isLoading: subInvLoading } =
    useOrganizationSubscriptionInvoicesQuery(organizationId);

  const mergedRows = useMemo((): MergedRow[] => {
    const orders: OrderDto[] =
      data?.pages
        ?.flatMap((page) => page.data)
        .flatMap((event) => event.orders || [])
        .filter((order) => order?.invoiceId !== undefined) ?? [];

    const orderRows: MergedRow[] = orders.map((order) => ({
      kind: 'order',
      order,
      sortAt: new Date(order.createdAt).getTime(),
    }));

    const subRows: MergedRow[] = (subscriptionInvoices ?? []).map((item) => ({
      kind: 'subscription',
      item,
      sortAt: new Date(item.createdAt).getTime(),
    }));

    return [...orderRows, ...subRows].sort((a, b) => b.sortAt - a.sortAt);
  }, [data?.pages, subscriptionInvoices]);

  const isLoading = ordersLoading || subInvLoading;

  if (isLoading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }
  return (
    <TableContainer component={Paper}>
      <MuiTable>
        <TableHead>
          <TableRow>
            <TableCell align="left">{`${t('organization.invoice.createdAt')}`}</TableCell>
            <TableCell align="left">{`${t('organization.invoice.type')}`}</TableCell>
            <TableCell align="right">{`${t('organization.invoice.totalPrice')}`}</TableCell>
            <TableCell align="right" sx={{ width: '120px' }}>{`${t('organization.invoice.status')}`}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {mergedRows.map((row) =>
            row.kind === 'order' ? (
              <Row key={`order-${row.order.id}`} order={row.order} />
            ) : (
              <SubscriptionInvoiceRow key={`sub-${row.item.id}`} organizationId={organizationId} item={row.item} />
            ),
          )}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
}
